import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Globe, Image as ImageIcon, Loader2, Save } from 'lucide-react';
import * as api from '../worlds.api';
import { toast } from '../worlds.store';
import { LEVEL_NAME_RE, fmtDate, tileUrlError } from '../constants';
import type { Level, LevelInput } from '../worlds.types';
import { Modal } from './Modal';
import { staticBackgroundFor } from '../map/staticBackgrounds';
import styles from '../../LootboxPage.module.scss';
import ws from '../WorldsPage.module.scss';
import lm from '../levelsMarkers.module.scss';

// ============================================================
// Niveau (monde Minecraft) : création et modification
// ============================================================
// Le nom est celui du monde côté serveur, immuable (clé de la grille, des marqueurs, des biomes et
// des régions). tileUrl est un gabarit Leaflet vers les tuiles RecubeDynmap, chargées en direct par
// le navigateur ; vide, la carte affiche l'image fixe du terrain si le niveau en a une
// (map/staticBackgrounds.ts), sinon un fond neutre. Noms d'origine : mondes NG Java qui
// correspondent à ce niveau (world, DIM-28).
// Mêmes bornes que le satellite (LevelService, WorldsValidation) : le serveur reste l'autorité.
// ============================================================

const TITLE_MAX = 64;
const SORT_MAX = 100000;
const MAX_SOURCE_NAMES = 8;

/** « world, DIM-28 » → ['world', 'DIM-28'] : doublons retirés sans tenir compte de la casse. */
function parseSourceNames(v: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of v.split(/[\s,;]+/)) {
    const n = raw.trim();
    if (!n || seen.has(n.toLowerCase())) continue;
    seen.add(n.toLowerCase());
    out.push(n);
  }
  return out;
}

function sameList(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

/** Tuiles de test : les 4 autour de l'origine au zoom le plus large (plus de chances d'exister). */
function testTiles(url: string, zoom: number): { key: string; src: string }[] {
  const out: { key: string; src: string }[] = [];
  for (const y of [-1, 0]) {
    for (const x of [-1, 0]) {
      out.push({
        key: `${x},${y}`,
        src: url.replace('{z}', String(zoom)).replace('{x}', String(x)).replace('{y}', String(y)),
      });
    }
  }
  return out;
}

/** Au-delà, une tuile qui n'a ni chargé ni échoué (hôte injoignable) compte comme absente. */
const TILE_TEST_TIMEOUT_MS = 8000;

function TileTest({ url, zoom }: { url: string; zoom: number }) {
  const tiles = useMemo(() => testTiles(url, zoom), [url, zoom]);
  const [state, setState] = useState<Record<string, 'ok' | 'ko'>>({});
  const done = Object.keys(state).length;
  const ok = Object.values(state).filter((v) => v === 'ok').length;
  const mark = (k: string, v: 'ok' | 'ko') => setState((s) => (s[k] ? s : { ...s, [k]: v }));
  useEffect(() => {
    const t = window.setTimeout(() => setState((s) => {
      const next = { ...s };
      tiles.forEach((x) => { if (!next[x.key]) next[x.key] = 'ko'; });
      return next;
    }), TILE_TEST_TIMEOUT_MS);
    return () => window.clearTimeout(t);
  }, [tiles]);
  return (
    <div className={lm.tileTest}>
      <div className={lm.tileMosaic} aria-label="Tuiles autour de l'origine">
        {tiles.map((t) => (state[t.key] === 'ko'
          ? <span key={t.key} className={lm.tileMissing} title={`Tuile ${t.key} introuvable`} />
          : <img key={t.key} src={t.src} alt="" title={t.src} onLoad={() => mark(t.key, 'ok')} onError={() => mark(t.key, 'ko')} />))}
      </div>
      <div className={lm.tileTestText}>
        {done < tiles.length ? (
          <span className={ws.inlineIcon}><Loader2 size={12} className={ws.spinning} /> Chargement des tuiles de test…</span>
        ) : ok > 0 ? (
          <span className={ws.textOk}>{ok} tuile(s) sur {tiles.length} trouvée(s) autour de l'origine (zoom {zoom}).</span>
        ) : (
          <span className={ws.textWarn}>
            Aucune tuile trouvée autour de l'origine (zoom {zoom}) : vérifie l'hôte, le nom du monde dans l'URL et le zoom max.
          </span>
        )}
        <span>Tuiles x -1 et 0, y -1 et 0 : le quart de monde le plus proche du point 0, 0.</span>
      </div>
    </div>
  );
}

export function LevelModal({ level, levels, onClose, onSaved }: {
  level: Level | null;
  /** Tous les niveaux : unicité des noms d'origine (le satellite contrôle aussi, 409). */
  levels: Level[];
  onClose: () => void;
  onSaved: (l: Level) => void;
}) {
  const [name, setName] = useState(level?.name ?? '');
  const [title, setTitle] = useState(level?.title ?? '');
  const [tileUrl, setTileUrl] = useState(level?.tileUrl ?? '');
  const [tileMaxZoom, setTileMaxZoom] = useState(String(level?.tileMaxZoom ?? 8));
  const [sortOrder, setSortOrder] = useState(String(level?.sortOrder ?? 0));
  const [sources, setSources] = useState((level?.sourceNames ?? []).join(', '));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [tried, setTried] = useState(false);
  const [testUrl, setTestUrl] = useState<{ url: string; zoom: number; n: number } | null>(null);

  const zoomOk = /^\d$/.test(tileMaxZoom.trim()) && Number(tileMaxZoom) <= 8;
  const sortOk = /^-?\d+$/.test(sortOrder.trim()) && Math.abs(Number(sortOrder)) <= SORT_MAX;
  const sourceList = useMemo(() => parseSourceNames(sources), [sources]);

  const sourceError = useMemo(() => {
    const bad = sourceList.find((n) => !LEVEL_NAME_RE.test(n));
    if (bad) return `${bad} : lettres, chiffres, - et _, commence par une lettre (64 au plus)`;
    if (sourceList.length > MAX_SOURCE_NAMES) return `${MAX_SOURCE_NAMES} noms au plus`;
    // Un nom que ce niveau porte déjà n'est pas contrôlé (même règle que le satellite).
    const own = new Set((level?.sourceNames ?? []).map((n) => n.toLowerCase()));
    for (const n of sourceList) {
      if (own.has(n.toLowerCase())) continue;
      const other = levels.find((l) => l.name !== level?.name && l.sourceNames.some((s) => s.toLowerCase() === n.toLowerCase()));
      if (other) return `${n} est déjà un nom d'origine du niveau ${other.title} (${other.name})`;
    }
    return null;
  }, [sourceList, levels, level]);

  const nameTaken = !level && levels.some((l) => l.name === name.trim());
  const errors = {
    name: !level && !LEVEL_NAME_RE.test(name)
      ? (name ? 'Lettres, chiffres, - et _, commence par une lettre' : 'Requis')
      : nameTaken ? 'Un niveau porte déjà ce nom' : null,
    title: !title.trim() ? 'Requis' : title.trim().length > TITLE_MAX ? `${TITLE_MAX} caractères au plus` : null,
    tileUrl: tileUrlError(tileUrl),
    tileMaxZoom: !zoomOk ? 'Entier de 0 à 8' : null,
    sortOrder: !sortOk ? `Entier entre -${SORT_MAX.toLocaleString('fr-FR')} et ${SORT_MAX.toLocaleString('fr-FR')}` : null,
    sources: sourceError,
  };
  type Field = keyof typeof errors;
  const valid = Object.values(errors).every((e) => !e);
  const show = (f: Field) => (tried || touched[f] ? errors[f] : null);
  const touch = (f: Field) => setTouched((t) => (t[f] ? t : { ...t, [f]: true }));

  const trimmedUrl = tileUrl.trim();
  const mixed = trimmedUrl.startsWith('http://') && window.location.protocol === 'https:';
  const canTest = !!trimmedUrl && !errors.tileUrl;
  // Fond d'un niveau sans URL : image fixe du terrain s'il en a une (nom saisi à la création).
  const emptyBg = staticBackgroundFor({ name: level?.name ?? name.trim(), tileUrl: null });

  const save = async () => {
    setTried(true);
    if (!valid || busy) return;
    const body: LevelInput = {
      title: title.trim(),
      tileUrl: trimmedUrl || null,
      tileMaxZoom: Number(tileMaxZoom),
      sortOrder: Number(sortOrder),
    };
    let sentSources: string[] | null = null;
    let res;
    setBusy(true);
    setError(null);
    if (level) {
      const diff: LevelInput = {};
      if (body.title !== level.title) diff.title = body.title;
      if (body.tileUrl !== level.tileUrl) diff.tileUrl = body.tileUrl;
      if (body.tileMaxZoom !== level.tileMaxZoom) diff.tileMaxZoom = body.tileMaxZoom;
      if (body.sortOrder !== level.sortOrder) diff.sortOrder = body.sortOrder;
      // Noms d'origine : envoyés seulement s'ils ont changé (ordre et casse comptent).
      if (!sameList(sourceList, level.sourceNames)) { diff.sourceNames = sourceList; sentSources = sourceList; }
      if (Object.keys(diff).length === 0) { setBusy(false); onClose(); return; }
      res = await api.patchLevel(level.name, diff);
    } else {
      if (sourceList.length > 0) { body.sourceNames = sourceList; sentSources = sourceList; }
      res = await api.createLevel({ ...body, name: name.trim() });
    }
    setBusy(false);
    if (!res.data) { setError(res.error ?? 'Enregistrement en échec'); return; }
    toast.success(level ? `Niveau ${res.data.name} mis à jour` : `Niveau ${res.data.name} créé`);
    // Satellite antérieur aux noms d'origine modifiables : le champ est ignoré sans erreur.
    if (sentSources && !sameList(res.data.sourceNames ?? [], sentSources)) {
      toast.warning('Noms d\'origine non enregistrés : le satellite ne gère pas encore leur modification.');
    }
    onSaved(res.data);
    onClose();
  };

  const err = (f: Field) => { const e = show(f); return e ? <span className={ws.fieldError}>{e}</span> : null; };
  const req = <span className={ws.req}> *</span>;

  return (
    <Modal title={level ? `Modifier le niveau ${level.title}` : 'Nouveau niveau'} onClose={onClose} busy={busy}
      icon={<Globe size={16} />} onSubmit={() => void save()}
      subtitle={level
        ? <>Modifié le {fmtDate(level.updatedAt)}{level.updatedBy ? ` par ${level.updatedBy}` : ''}</>
        : 'Monde Minecraft qui porte une grille de pays, des marqueurs et des régions.'}
      footer={(
        <>
          {error && <span className={ws.footError}><AlertTriangle size={13} /> {error}</span>}
          <button type="button" className={styles.secondaryBtn} onClick={onClose} disabled={busy}>Annuler</button>
          <button type="submit" className={styles.primaryBtn} disabled={busy || (tried && !valid)}>
            {busy ? <Loader2 size={14} className={styles.spin} /> : <Save size={14} />} {level ? 'Enregistrer' : 'Créer'}
          </button>
        </>
      )}>
      <div className={ws.formGrid}>
        <label className={styles.field} style={{ minWidth: 0 }}>
          <span className={styles.label}>Nom technique{!level && req}</span>
          <input className={`${styles.input} ${ws.mono}`} value={name} disabled={!!level} maxLength={64} placeholder="overworld"
            autoFocus={!level} spellCheck={false} aria-invalid={!!show('name') || undefined}
            onChange={(e) => setName(e.target.value.replace(/[^A-Za-z0-9_-]/g, ''))} onBlur={() => touch('name')} />
          {level
            ? <span className={ws.fieldHint}>Immuable : nom du monde côté serveur.</span>
            : err('name') ?? <span className={ws.fieldHint}>Nom du monde côté serveur, ex. overworld, nether, the_end.</span>}
        </label>
        <label className={styles.field} style={{ minWidth: 0 }}>
          <span className={styles.label}>Titre{req}</span>
          <input className={styles.input} value={title} maxLength={TITLE_MAX} placeholder="Terre" autoFocus={!!level}
            aria-invalid={!!show('title') || undefined}
            onChange={(e) => setTitle(e.target.value)} onBlur={() => touch('title')} />
          {err('title') ?? <span className={ws.fieldHint}>Affiché dans le sélecteur de niveau ({TITLE_MAX} caractères au plus).</span>}
        </label>
        <label className={styles.field} style={{ minWidth: 0 }}>
          <span className={styles.label}>Zoom max des tuiles</span>
          <input className={styles.input} inputMode="numeric" value={tileMaxZoom} aria-invalid={!!show('tileMaxZoom') || undefined}
            onChange={(e) => setTileMaxZoom(e.target.value)} onBlur={() => touch('tileMaxZoom')} />
          {err('tileMaxZoom') ?? <span className={ws.fieldHint}>config.maxZoom de RecubeDynmap (8 par défaut).</span>}
        </label>
        <label className={styles.field} style={{ minWidth: 0 }}>
          <span className={styles.label}>Ordre</span>
          <input className={styles.input} inputMode="numeric" value={sortOrder} aria-invalid={!!show('sortOrder') || undefined}
            onChange={(e) => setSortOrder(e.target.value)} onBlur={() => touch('sortOrder')} />
          {err('sortOrder') ?? <span className={ws.fieldHint}>Rang dans le sélecteur (croissant).</span>}
        </label>
      </div>

      <label className={styles.field} style={{ minWidth: 0 }}>
        <span className={styles.label}>Noms d'origine</span>
        <input className={`${styles.input} ${ws.mono}`} value={sources} spellCheck={false} placeholder="world, DIM-28"
          aria-invalid={!!show('sources') || undefined}
          onChange={(e) => setSources(e.target.value)}
          onBlur={() => { touch('sources'); if (!sourceError) setSources(sourceList.join(', ')); }} />
        {err('sources') ?? (
          <span className={ws.fieldHint}>
            Mondes NG Java qui correspondent à ce niveau, séparés par des virgules ({MAX_SOURCE_NAMES} au plus). Un nom
            n'appartient qu'à un niveau.
          </span>
        )}
      </label>

      <div className={styles.field} style={{ minWidth: 0 }}>
        <label className={styles.label} htmlFor="worlds-level-tiles">URL des tuiles</label>
        <div className={ws.inlineActions} style={{ flexWrap: 'nowrap' }}>
          <input id="worlds-level-tiles" className={`${styles.input} ${ws.mono}`} style={{ flex: 1, minWidth: 0 }}
            value={tileUrl} maxLength={500} spellCheck={false} aria-invalid={!!show('tileUrl') || undefined}
            placeholder="https://carte.exemple.gg/tiles/overworld/{z}/{x},{y}.png"
            onChange={(e) => { setTileUrl(e.target.value); setTestUrl(null); }} onBlur={() => touch('tileUrl')} />
          <button type="button" className={styles.secondaryBtn} disabled={!canTest}
            title={canTest ? 'Charger 4 tuiles autour de l\'origine' : 'Renseigne une URL valide pour la tester'}
            onClick={() => setTestUrl((t) => ({ url: trimmedUrl, zoom: zoomOk ? Number(tileMaxZoom) : 8, n: (t?.n ?? 0) + 1 }))}>
            <ImageIcon size={14} /> Tester
          </button>
        </div>
        {err('tileUrl') ?? (mixed ? (
          <span className={`${ws.fieldHint} ${ws.textWarn}`}>
            URL en http : le panel étant en https, le navigateur bloquera les tuiles (contenu mixte). Utilise une URL https.
          </span>
        ) : (
          <span className={ws.fieldHint}>
            URL https publique des tuiles RecubeDynmap, ex. https://carte.exemple.gg/tiles/overworld/{'{z}'}/{'{x}'},{'{y}'}.png ;
            vide = {emptyBg ? `image fixe du terrain ${emptyBg.origin} en attendant les tuiles` : 'fond neutre'}.
          </span>
        ))}
        {testUrl && <TileTest key={`${testUrl.url}|${testUrl.zoom}|${testUrl.n}`} url={testUrl.url} zoom={testUrl.zoom} />}
      </div>
    </Modal>
  );
}
